import { useEffect, useRef, useState } from 'react';
import { useDatabase } from './useDatabase';

export const useTableData = (tableName: string, idName: string = 'id') => {
  const db = useDatabase();
  const dbRef = useRef(db);
  const [data, setData] = useState<any>([]);
  const [error, setError] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Update ref when db changes
  useEffect(() => {
    dbRef.current = db;
  }, [db]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Use the appropriate repository based on table name
        let result;
        switch (tableName) {
          case 'user':
            result = await dbRef.current.user.getAllData(tableName, true);
            break;
          case 'classes':
            result = await dbRef.current.class.getAllClasses();
            break;
          case 'students':
            result = await dbRef.current.student.getAllStudents();
            break;
          case 'semester':
            result = await dbRef.current.semester.getAllSemesters();
            break;
          default:
            // Fallback to generic method for any other table
            result = await dbRef.current.user.getAllData(tableName, true);
        }

        if (result.error) {
          setError(result.error);
          setData([]);
        } else {
          // Sort data by the specified idName field (preserving original behavior)
          const sortedData =
            result.data?.sort((a: any, b: any) => {
              const aValue = a[idName];
              const bValue = b[idName];
              if (aValue < bValue) return -1;
              if (aValue > bValue) return 1;
              return 0;
            }) || [];

          setData(sortedData);
        }
      } catch (err) {
        setError(err);
        setData([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableName, idName]);

  return { data, error, isLoading };
};

export const useTableSelectedData = (
  tableName: string,
  columns: string,
  idName: string = 'id',
) => {
  const db = useDatabase();
  const dbRef = useRef(db);
  const [data, setData] = useState<any>([]);
  const [error, setError] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Update ref when db changes
  useEffect(() => {
    dbRef.current = db;
  }, [db]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // For selected columns, we need to use the direct Supabase query
        // This preserves the exact behavior of the original function
        const result = await dbRef.current.user.supabaseClient
          .from(tableName)
          .select(columns)
          .eq('deleted', 'false')
          .order(idName, { ascending: true });

        if (result.error) {
          setError(result.error);
          setData([]);
        } else {
          setData(result.data || []);
        }
      } catch (err) {
        setError(err);
        setData([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableName, columns, idName]);

  return { data, error, isLoading };
};
